import { type JSX } from "react";
import { Link } from "react-router";
import { type RowSelectionState } from "@tanstack/react-table";
import { ListIllustration } from "vega-illustrations";

import { CoreTable } from "@vega-core/components/Table/CoreTable/CoreTable";
import { m } from "@vega-core/paraglide/messages";
import { richTextFormat } from "@vega-core/utils/i18nHelpers";
import { CoInvestmentFullPaths } from "@vega-modules/paths";

import { type ProspectWithMemberCount } from "./hooks/useOutreachManagement";
import { useOutreachTableColumns } from "./hooks/useOutreachTableColumns";
import { OutreachTableColumnId } from "./constants";

type OutreachTableProps = {
  prospectsData: ProspectWithMemberCount[];
  isLoading: boolean;
  onShare?: (rowIndex: number) => void;
  onCopyLink?: (rowIndex: number) => void;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: (
    updater:
      | RowSelectionState
      | ((old: RowSelectionState) => RowSelectionState),
  ) => void;
  onAddOpportunityOwner?: (prospectIds: string[]) => void;
  onUpdateDataPoints?: (rowIndex: number) => void;
  onOpenContactsModal?: (rowIndex: number) => void;
  dealId: string;
  paginationElement?: JSX.Element | null;
};

export const OutreachTable = ({
  prospectsData,
  isLoading,
  onShare,
  onCopyLink,
  rowSelection = {},
  onRowSelectionChange,
  onAddOpportunityOwner,
  onUpdateDataPoints,
  onOpenContactsModal,
  dealId,
  paginationElement,
}: OutreachTableProps) => {
  const columns = useOutreachTableColumns({
    onShare,
    onCopyLink,
    onAddOpportunityOwner,
    onUpdateDataPoints,
    onOpenContactsModal,
  });

  return (
    <CoreTable
      columns={columns}
      data={prospectsData}
      dataTestId="outreach-table"
      emptyState={{
        title: m.coInvestments_outreach_noProspects_title(),
        description: richTextFormat(
          m.coInvestments_outreach_noProspects_description(),
          {
            link: ({ children }) => (
              <Link
                className="text-text/brand underline"
                to={CoInvestmentFullPaths.CoInvestmentProspects.replace(
                  ":id",
                  dealId,
                )}
              >
                {children}
              </Link>
            ),
          },
        ),
        illustration: <ListIllustration />,
      }}
      enableRowSelection
      getRowId={(row) => row.id}
      initialSorting={[{ id: OutreachTableColumnId.Tier, desc: false }]}
      isLoading={isLoading}
      noSorting={false}
      onRowSelectionChange={onRowSelectionChange}
      paginationElement={paginationElement}
      selectedRows={rowSelection}
    />
  );
};
